import { watchEffect, type Directive, type DirectiveBinding, type WatchStopHandle } from 'vue'
import { useAuthStore } from '@/stores/auth'

type PermissionValue = string | string[] | undefined

interface PermissionState {
  value: PermissionValue
  any: boolean
  disable: boolean
  wasDisabled: boolean
  stop: WatchStopHandle
}

const states = new WeakMap<HTMLElement, PermissionState>()

export function isPermissionAllowed(
  has: (permission: string) => boolean,
  value: PermissionValue,
  any = false,
): boolean {
  if (!value || (Array.isArray(value) && value.length === 0)) return true
  const required = Array.isArray(value) ? value : [value]
  return any ? required.some(permission => has(permission)) : required.every(permission => has(permission))
}

function apply(el: HTMLElement, state: PermissionState): void {
  const auth = useAuthStore()
  const permitted = isPermissionAllowed(auth.has, state.value, state.any)
  el.toggleAttribute('data-permission-denied', !permitted)
  if (state.disable) {
    if (!permitted) {
      if (!state.wasDisabled) {
        state.wasDisabled = true
        el.setAttribute('aria-disabled', 'true')
        ;(el as HTMLButtonElement).disabled = true
      }
    } else if (state.wasDisabled) {
      state.wasDisabled = false
      el.removeAttribute('aria-disabled')
      ;(el as HTMLButtonElement).disabled = false
    }
    return
  }
  if (!permitted) {
    el.setAttribute('hidden', '')
    el.setAttribute('aria-hidden', 'true')
  } else {
    el.removeAttribute('hidden')
    el.removeAttribute('aria-hidden')
  }
}

function bind(el: HTMLElement, binding: DirectiveBinding<PermissionValue>): void {
  states.get(el)?.stop()
  const state: PermissionState = {
    value: binding.value,
    any: Boolean(binding.modifiers.any),
    disable: Boolean(binding.modifiers.disable),
    wasDisabled: (el as HTMLButtonElement).disabled === true,
    stop: () => undefined,
  }
  // Read auth state inside the effect so role previews, session renewal and
  // permission revocation update already-mounted controls without a rerender.
  state.stop = watchEffect(() => {
    const auth = useAuthStore()
    void auth.session
    void auth.viewAsRole
    apply(el, state)
  })
  states.set(el, state)
}

function update(el: HTMLElement, binding: DirectiveBinding<PermissionValue>): void {
  const state = states.get(el)
  if (!state) {
    bind(el, binding)
    return
  }
  state.value = binding.value
  state.any = Boolean(binding.modifiers.any)
  state.disable = Boolean(binding.modifiers.disable)
  apply(el, state)
}

function release(el: HTMLElement): void {
  states.get(el)?.stop()
  states.delete(el)
}

/**
 * Front-end visibility/disable helper. It never replaces the route guard or
 * service authorization; those remain the real enforcement layers.
 *
 * v-permission="'users.create'"  hide unless allowed
 * v-permission.any="['a', 'b']"   hide unless one is allowed
 * v-permission.disable="'x'"      keep the control visible but disabled
 */
export const vPermission: Directive<HTMLElement, PermissionValue> = {
  mounted: bind,
  updated: update,
  unmounted: release,
}

export const permissionDirective = {
  install(app: { directive: (name: string, directive: Directive) => void }): void {
    app.directive('permission', vPermission)
  },
}
